import { useCallback } from 'react';

import { ViewType } from '@this+that/models';
import log from 'loglevel';

import { resolveListingEmailAddresses } from './resolveListingEmailAddresses';
import { LABEL_PREFIXES } from '../../../components/InboxLabelsTabsFilter/constants';
import { useGetPaginatedUserEmailCommunications } from '../../../services/userCommunications.service';
import {
  useIntegrationsStore,
  useUserCommunicationsStore,
} from '../../../store';
import { hasActiveRemovedEmailAccounts } from '../../../store/Inbox/Integrations/removedEmailAccounts';

const getFetchGeneration = (): number =>
  useUserCommunicationsStore.getState().fetchGeneration;

const isSameFilter = (a?: string[], b?: string[]): boolean => {
  if (a === undefined || b === undefined) return a === b;
  if (a.length !== b.length) return false;
  const sortedB = [...b].sort();
  return [...a].sort().every((address, index) => address === sortedB[index]);
};

const isResponseStale = (
  requestGeneration: number,
  requestFilters: string[] | undefined,
  isScopedToAccountFilter: boolean,
): boolean =>
  requestGeneration !== getFetchGeneration() ||
  (isScopedToAccountFilter &&
    !isSameFilter(
      requestFilters,
      useIntegrationsStore.getState().currentEmailFilters,
    ));

interface FetchUserCommunicationParams {
  limit: number;
  isInitialFetch?: boolean;
  isScopedToAccountFilter?: boolean;
}

export const useFetchIndividualUserCommunications = ({
  limit,
  isInitialFetch = false,
  isScopedToAccountFilter = true,
}: FetchUserCommunicationParams) => {
  const { getPaginatedUserEmailCommunications } =
    useGetPaginatedUserEmailCommunications();

  const setLoading = useUserCommunicationsStore((state) => state.setLoading);
  const saveIndividualUserCommunications = useUserCommunicationsStore(
    (state) => state.saveIndividualUserCommunications,
  );
  const startKeys = useUserCommunicationsStore((state) => state.startKeys);
  const markViewTypesSynced = useUserCommunicationsStore(
    (state) => state.markViewTypesSynced,
  );

  const handleFetchIndividualUserCommunications = useCallback(
    async (
      viewType: ViewType | ViewType[],
      isLoadMore?: boolean,
      emailAddresses?: string[],
    ) => {
      const { integrationAccounts, removedEmailAccounts } =
        useIntegrationsStore.getState();
      const resolvedEmailAddresses = resolveListingEmailAddresses(
        emailAddresses,
        integrationAccounts,
        hasActiveRemovedEmailAccounts(removedEmailAccounts, Date.now()),
      );

      if (
        resolvedEmailAddresses !== undefined &&
        resolvedEmailAddresses.length === 0
      ) {
        const viewTypes = Array.isArray(viewType) ? viewType : [viewType];
        viewTypes.forEach((vt) =>
          saveIndividualUserCommunications([], null, vt, false),
        );
        if (!isLoadMore) {
          markViewTypesSynced(viewTypes);
        }
        return;
      }

      setLoading(true);

      const viewTypes = Array.isArray(viewType) ? viewType : [viewType];
      const requestGeneration = getFetchGeneration();
      const isBatchFetch = viewTypes.length > 1;

      try {
        const { startKeys: currentStartKeys } =
          useUserCommunicationsStore.getState();
        let exclusiveStartKey: string | undefined;

        if (isInitialFetch || !isLoadMore) {
          exclusiveStartKey = undefined;
        } else if (isBatchFetch) {
          exclusiveStartKey = currentStartKeys.primary || undefined;
        } else if (viewTypes[0].startsWith(LABEL_PREFIXES.GMAIL_LABEL)) {
          const labelId = viewTypes[0].replace(LABEL_PREFIXES.GMAIL_LABEL, '');
          exclusiveStartKey = currentStartKeys.labels[labelId] || undefined;
        } else {
          const keyMap: Record<ViewType, string | null> = {
            PRIMARY: currentStartKeys.primary,
            PROMOTIONS: currentStartKeys.promotions,
            SOCIAL: currentStartKeys.social,
            UPDATES: currentStartKeys.updates,
            FORUMS: currentStartKeys.forums,
            DRAFT: currentStartKeys.draft,
            SENT: currentStartKeys.sent,
            SPAM: currentStartKeys.spam,
            TRASH: currentStartKeys.trash,
            ALL_EMAILS: currentStartKeys.all,
          } as const;

          const singleViewType = viewTypes[0];
          if (keyMap[singleViewType] !== undefined) {
            exclusiveStartKey = keyMap[singleViewType] || undefined;
          } else {
            exclusiveStartKey =
              currentStartKeys.labels[singleViewType] || undefined;
          }
        }

        const storeViewType = viewTypes[0];

        const response = await getPaginatedUserEmailCommunications({
          limit,
          viewTypes,
          exclusiveStartKey,
          emailAddresses: resolvedEmailAddresses,
        });

        if (!response) {
          log.warn('No response received when fetching user communications');
          return;
        }

        if (
          isResponseStale(
            requestGeneration,
            emailAddresses,
            isScopedToAccountFilter,
          )
        ) {
          return;
        }

        const { userCommunications, exclusiveStartKey: newExclusiveStartKey } =
          response;

        saveIndividualUserCommunications(
          userCommunications,
          newExclusiveStartKey,
          storeViewType,
          isLoadMore,
          viewTypes,
        );
      } catch (error) {
        log.error('Error fetching user communications:', error);
      } finally {
        setLoading(false);
        if (
          !isLoadMore &&
          !isResponseStale(
            requestGeneration,
            emailAddresses,
            isScopedToAccountFilter,
          )
        ) {
          markViewTypesSynced(viewTypes);
        }
      }
    },
    [
      getPaginatedUserEmailCommunications,
      isInitialFetch,
      isScopedToAccountFilter,
      limit,
      markViewTypesSynced,
      saveIndividualUserCommunications,
      setLoading,
    ],
  );

  const getHasMore = (viewType: ViewType): boolean => {
    if (viewType.startsWith(LABEL_PREFIXES.GMAIL_LABEL)) {
      const labelId = viewType.replace(LABEL_PREFIXES.GMAIL_LABEL, '');
      return Boolean(startKeys.labels[labelId]);
    }

    const keyMap: Record<
      Exclude<ViewType, `Label_${string}`>,
      string | null
    > = {
      PRIMARY: startKeys.primary,
      PROMOTIONS: startKeys.promotions,
      SOCIAL: startKeys.social,
      UPDATES: startKeys.updates,
      FORUMS: startKeys.forums,
      DRAFT: startKeys.draft,
      SENT: startKeys.sent,
      SPAM: startKeys.spam,
      TRASH: startKeys.trash,
      ALL_EMAILS: startKeys.all,
    };

    const fixedViewTypeKey =
      keyMap[viewType as Exclude<ViewType, `Label_${string}`>];
    if (fixedViewTypeKey !== undefined) {
      return Boolean(fixedViewTypeKey);
    }

    return Boolean(startKeys.labels[viewType]);
  };

  return {
    handleFetchIndividualUserCommunications,
    getHasMore,
  };
};
